"use client"

import { useState, useEffect } from "react"
import { 
  Plus, Loader2, Table, Edit, Trash2, Save, 
  AlertCircle, Type, Hash, Calendar, ToggleLeft,
  Info, CheckCircle2, XCircle, ArrowLeft
} from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { useToast } from "../../../components/ui/use-toast"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog'
import { Label } from '../../../components/ui/label'
import { 
  getCustomInventoryColumns,
  createCustomInventoryColumn,
  updateCustomInventoryColumn,
  deleteCustomInventoryColumn
} from "../../../../ApiCalls/mantisFlowsConfiguration"
import { getInventoryItems } from "../../../../ApiCalls/mantisFlowsInventory"

const COLUMN_TYPES = [
  { 
    id: 'VARCHAR(255)', 
    name: 'Text', 
    icon: Type,
    description: 'For text values like names, descriptions, or notes',
    color: 'blue'
  },
  { 
    id: 'INTEGER', 
    name: 'Number', 
    icon: Hash,
    description: 'For numeric values like quantities, prices, or measurements',
    color: 'green'
  },
  { 
    id: 'DATE', 
    name: 'Date', 
    icon: Calendar,
    description: 'For date values like expiration dates or purchase dates',
    color: 'purple'
  },
  { 
    id: 'BOOLEAN', 
    name: 'Boolean', 
    icon: ToggleLeft,
    description: 'For yes/no or true/false values',
    color: 'orange'
  }
]

export default function CustomColumnsModal({ clientId, onClose, embedded = false }) {
  const [columns, setColumns] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('list') // 'list' or 'form'
  const [editingColumn, setEditingColumn] = useState(null)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, column: null })
  const [formData, setFormData] = useState({
    column_name: '',
    display_name: '',
    data_type: 'VARCHAR(255)',
    description: '',
    is_required: false,
    is_visible: true,
    display_order: 0
  })
  const [selectedType, setSelectedType] = useState(null)
  
  const { toast } = useToast()
  
  useEffect(() => {
    fetchColumns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  const fetchColumns = async () => {
    try {
      setLoading(true)
      const response = await getCustomInventoryColumns(clientId)
      if (response.success) {
        setColumns(response.data)
      }
    } catch (error) {
      console.error("Error fetching custom columns:", error)
      toast({
        title: "Error",
        description: "Failed to load custom columns",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const refreshInventory = async () => {
    try {
      await getInventoryItems(clientId)
    } catch (error) {
      console.error("Error refreshing inventory:", error)
    }
  }
  
  const handleCreateColumn = async () => {
    try {
      setLoading(true)
      const response = await createCustomInventoryColumn(clientId, formData)
      if (response.success) {
        toast({
          title: "Success",
          description: "Custom column created successfully",
          variant: "default"
        })
        setView('list')
        resetForm()
        await fetchColumns()
        await refreshInventory()
      }
    } catch (error) {
      console.error("Error creating custom column:", error)
      toast({
        title: "Error",
        description: "Failed to create custom column",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }
  
  const handleUpdateColumn = async () => {
    try {
      setLoading(true)
      const response = await updateCustomInventoryColumn(clientId, editingColumn.id, formData)
      if (response.success) {
        toast({
          title: "Success",
          description: "Custom column updated successfully",
          variant: "default"
        })
        setView('list')
        resetForm()
        await fetchColumns()
        await refreshInventory()
      }
    } catch (error) {
      console.error("Error updating custom column:", error)
      toast({
        title: "Error",
        description: "Failed to update custom column",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }
  
  const handleDeleteColumn = async () => {
    try {
      setLoading(true)
      const response = await deleteCustomInventoryColumn(clientId, deleteDialog.column.id)
      if (response.success) {
        toast({
          title: "Success",
          description: "Custom column deleted successfully",
          variant: "default"
        })
        setDeleteDialog({ open: false, column: null })
        await fetchColumns()
        await refreshInventory()
      }
    } catch (error) {
      console.error("Error deleting custom column:", error)
      toast({
        title: "Error",
        description: "Failed to delete custom column",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }
  
  const resetForm = () => {
    setFormData({
      column_name: '',
      display_name: '',
      data_type: 'VARCHAR(255)',
      description: '',
      is_required: false,
      is_visible: true,
      display_order: 0
    })
    setEditingColumn(null)
    setSelectedType(null)
  }
  
  const openCreateForm = () => {
    resetForm()
    setView('form')
  }
  
  const openEditForm = (column) => {
    setEditingColumn(column)
    setFormData({
      column_name: column.column_name,
      display_name: column.display_name,
      data_type: column.data_type,
      description: column.description || '',
      is_required: column.is_required,
      is_visible: column.is_visible,
      display_order: column.display_order
    })
    setSelectedType(COLUMN_TYPES.find(t => t.id === column.data_type))
    setView('form')
  }

  const handleTypeSelect = (type) => {
    setSelectedType(type)
    setFormData(prev => ({ ...prev, data_type: type.id }))
  }

  const renderListView = () => (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
            Custom Columns
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Add custom fields to your inventory items to track additional information
          </p>
        </div>
        <Button
          onClick={openCreateForm}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Column
        </Button>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
          <div>
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              About Custom Columns
            </h4>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
              Custom columns allow you to add additional fields to your inventory items. 
              These fields will appear in your inventory table and can be used to store 
              any type of information you need.
            </p>
          </div>
        </div>
      </div>
      
      {/* Columns Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : columns.length === 0 ? (
          <div className="col-span-full text-center p-8 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700">
            <Table className="h-12 w-12 mx-auto mb-3 text-slate-400" />
            <p className="text-lg text-slate-600 dark:text-slate-400">No custom columns yet</p>
            <p className="text-sm text-slate-500 mt-1">Create your first custom column to get started</p>
          </div>
        ) : (
          columns.map((column) => {
            const type = COLUMN_TYPES.find(t => t.id === column.data_type)
            return (
              <div 
                key={column.id} 
                className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow flex flex-col max-w-xs mx-auto min-h-[170px]"
              >
                <div className="flex items-start justify-between w-full">
                  <div className="flex items-center min-w-0">
                    {type?.icon && (
                      <div className={`p-2 rounded bg-${type.color}-100 dark:bg-${type.color}-900/30 mr-3`}>
                        <type.icon className={`h-5 w-5 text-${type.color}-600 dark:text-${type.color}-400`} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 truncate">
                        {column.display_name}
                      </h4>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                        {type?.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between ml-2 gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openEditForm(column)}
                      className="hover:bg-slate-100 dark:hover:bg-slate-700 p-1"
                      style={{ lineHeight: 1 }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteDialog({ open: true, column })}
                      className="hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 p-1"
                      style={{ lineHeight: 1 }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {column.description && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {column.description}
                  </p>
                )}
                <div className="mt-3 flex items-center gap-2">
                  {column.is_required ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Required
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-400">
                      <XCircle className="h-3 w-3 mr-1" />
                      Optional
                    </span>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )

  const renderFormView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setView('list')}
          className="hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
            {editingColumn ? 'Edit Custom Column' : 'New Custom Column'}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {editingColumn 
              ? 'Modify the properties of this custom column'
              : 'Create a new custom column for your inventory items'
            }
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Column Name */}
        <div className="space-y-2">
          <Label htmlFor="column_name">Column Name</Label>
          <Input
            id="column_name"
            value={formData.column_name}
            onChange={(e) => setFormData({ ...formData, column_name: e.target.value })}
            placeholder="Enter the database column name (e.g., product_size)"
            className="w-full"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            This will be used as the database column name. Use lowercase letters and underscores.
          </p>
        </div>

        {/* Display Name */}
        <div className="space-y-2">
          <Label htmlFor="display_name">Display Name</Label>
          <Input
            id="display_name"
            value={formData.display_name}
            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            placeholder="Enter the display name (e.g., Product Size)"
            className="w-full"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            This is the name that will be shown in the interface.
          </p>
        </div>
        
        {/* Column Type Selection */}
        <div className="space-y-3">
          <Label>Column Type</Label>
          <div className="grid grid-cols-2 gap-3">
            {COLUMN_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => handleTypeSelect(type)}
                className={`p-4 rounded border-2 transition-all ${
                  selectedType?.id === type.id
                    ? `border-${type.color}-500 bg-${type.color}-50 dark:bg-${type.color}-900/20`
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded bg-${type.color}-100 dark:bg-${type.color}-900/30`}>
                    <type.icon className={`h-5 w-5 text-${type.color}-600 dark:text-${type.color}-400`} />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-900 dark:text-slate-100">
                      {type.name}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {type.description}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description (Optional)</Label>
          <Input
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Add a description to help users understand this column"
            className="w-full"
          />
        </div>
        
        {/* Display Order */}
        <div className="space-y-2">
          <Label htmlFor="display_order">Display Order</Label>
          <Input
            id="display_order"
            type="number"
            value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            placeholder="Enter the display order"
            className="w-full"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Lower numbers will appear first in the table.
          </p>
        </div>
        
        {/* Toggles */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_required"
              checked={formData.is_required}
              onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
              className="rounded border-slate-300 dark:border-slate-600"
            />
            <Label htmlFor="is_required">Make this field required</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_visible"
              checked={formData.is_visible}
              onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
              className="rounded border-slate-300 dark:border-slate-600"
            />
            <Label htmlFor="is_visible">Show this field in the table</Label>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button
          variant="outline"
          onClick={() => setView('list')}
        >
          Cancel
        </Button>
        <Button
          onClick={editingColumn ? handleUpdateColumn : handleCreateColumn}
          disabled={!formData.column_name.trim() || !formData.display_name.trim() || !selectedType || loading}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {editingColumn ? 'Save Changes' : 'Create Column'}
        </Button>
      </div>
    </div>
  )
  
  return (
    <div className="space-y-6">
      {view === 'list' ? renderListView() : renderFormView()}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, column: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertCircle className="h-5 w-5 mr-2" />
              Delete Custom Column
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-600 dark:text-slate-400">
              Are you sure you want to delete the custom column "{deleteDialog.column?.display_name}"?
              This action cannot be undone and will remove this field from all inventory items.
            </p>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, column: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteColumn}
              disabled={loading}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Column
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
} 